import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDrillSchedules,
  getDrillScheduleById,
  createDrillSchedule,
  updateDrillSchedule,
  deleteDrillSchedule,
  type DrillScheduleFilters,
  type DrillSchedule,
} from '../../services';

export const drillScheduleKeys = {
  all: ['drillSchedules'] as const,
  lists: () => [...drillScheduleKeys.all, 'list'] as const,
  list: (filters: DrillScheduleFilters) =>
    [...drillScheduleKeys.lists(), filters] as const,
  details: () => [...drillScheduleKeys.all, 'detail'] as const,
  detail: (id: number) => [...drillScheduleKeys.details(), id] as const,
};

export function useDrillSchedules(filters: DrillScheduleFilters) {
  return useQuery({
    queryKey: drillScheduleKeys.list(filters),
    queryFn: () => getDrillSchedules(filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useDrillSchedule(id: number) {
  return useQuery({
    queryKey: drillScheduleKeys.detail(id),
    queryFn: () => getDrillScheduleById(id),
    enabled: !!id,
  });
}

export function useCreateDrillSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createDrillSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drillScheduleKeys.lists() });
    },
  });
}

export function useUpdateDrillSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: Partial<DrillSchedule>;
    }) => updateDrillSchedule(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: drillScheduleKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: drillScheduleKeys.detail(id),
      });
    },
  });
}

export function useDeleteDrillSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteDrillSchedule,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: drillScheduleKeys.lists() });
    },
  });
}
